import { useCallback, useEffect, useMemo, useState } from 'react';
import { panelSideNote, type AreaDto, type PanelDto } from '@app/shared';
import { api } from '../api';
import { useApp } from '../state';
import { Icon, LinkStatus, PageHead, plural } from '../ui';
import { TopBar } from './TopBar';
import { AreaFilter, loadFilter, saveFilter, useAreaMatch } from './AreaFilter';

/** Путь участка: «Цех 1 · Линия сборки». */
export function areaPath(areas: AreaDto[], id: string | null): string {
  if (!id) return 'Без участка';
  const byId = new Map(areas.map((a) => [a.id, a]));
  const parts: string[] = [];
  let cur = byId.get(id);
  for (let i = 0; cur && i < 10; i++) {
    parts.unshift(cur.name);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return parts.join(' · ') || 'Без участка';
}

export function Panels() {
  const { open, fail, can } = useApp();
  const [panels, setPanels] = useState<PanelDto[] | null>(null);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [q, setQ] = useState('');
  const [area, setAreaState] = useState<string | null>(() => loadFilter('panels'));
  const setArea = (v: string | null) => {
    setAreaState(v);
    saveFilter('panels', v);
  };
  const match = useAreaMatch(areas, area);

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([api.panels(), api.areas()]);
      setPanels(p);
      setAreas(a);
    } catch (e) {
      fail(e);
    }
  }, [fail]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (panels ?? []).filter(
      (p) => match(p.areaId) && (!needle || `${p.name} ${p.description ?? ''} ${areaPath(areas, p.areaId)}`.toLowerCase().includes(needle)),
    );
    const map = new Map<string, PanelDto[]>();
    for (const p of list) {
      const k = areaPath(areas, p.areaId);
      map.set(k, [...(map.get(k) ?? []), p]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, 'ru'));
  }, [panels, areas, q, match]);

  const online = panels?.filter((p) => p.link.ok === true).length ?? 0;

  return (
    <div className="stack">
      <TopBar />
      <PageHead title="Панели">
        {panels && panels.length > 0 && (
          <span className="small muted" style={{ paddingBottom: 2 }}>
            на связи {online} из {panels.length}
          </span>
        )}
      </PageHead>

      {panels && panels.length > 0 && <AreaFilter areas={areas} used={panels.map((p) => p.areaId)} value={area} onChange={setArea} />}

      {panels && panels.length > 5 && (
        <div className="search">
          <Icon name="search" size={18} />
          <input className="input" type="search" placeholder="Поиск: станок, линия" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск панели" />
        </div>
      )}

      {!panels && (
        <>
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
        </>
      )}

      {panels && panels.length === 0 && (
        <div className="card empty">
          <span className="mark">
            <Icon name="panels" />
          </span>
          <h2 className="h2">Панелей пока нет</h2>
          {can('panel.manage') ? (
            <>
              <p className="muted" style={{ margin: 0 }}>
                Добавьте панель: адрес ПК, к которому подключены панели, и порт конкретной машины.
              </p>
              <button type="button" className="btn btn-primary" onClick={() => open({ name: 'equipment' })}>
                Добавить панель
              </button>
            </>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              Вам ещё не открыли участки. Обратитесь к мастеру или администратору.
            </p>
          )}
        </div>
      )}

      {groups.map(([area, list]) => (
        <section key={area} className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">
            {area} · {list.length} {plural(list.length, 'панель', 'панели', 'панелей')}
          </h2>
          <div className="list">
            {list.map((p) => (
              <button key={p.id} type="button" className="item" onClick={() => open({ name: 'viewer', panelId: p.id })}>
                <span className="mark">
                  <Icon name="panels" />
                </span>
                <span className="grow stack" style={{ gap: 3 }}>
                  <span className="title ellipsis">{p.name}</span>
                  {p.description && <span className="sub ellipsis">{p.description}</span>}
                  <span className="row" style={{ gap: 8 }}>
                    {panelSideNote(p, panels ?? []) && <small className="side-note">{panelSideNote(p, panels ?? [])} ·</small>}
                    <LinkStatus link={p.link} />
                    {!p.canControl && (
                      <span className="badge outline">
                        <Icon name="eye" size={13} />
                        просмотр
                      </span>
                    )}
                  </span>
                </span>
                {can('panel.manage') ? (
                  <span
                    role="button"
                    tabIndex={0}
                    className="icon-btn"
                    aria-label={`Настройки панели ${p.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      open({ name: 'equipment', panelId: p.id });
                    }}
                  >
                    <Icon name="gear" size={18} />
                  </span>
                ) : (
                  <span className="chev">
                    <Icon name="right" size={18} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {panels && panels.length > 0 && groups.length === 0 && <p className="muted">{q ? `Ничего не нашлось по запросу «${q}».` : 'На этом участке панелей нет.'}</p>}
    </div>
  );
}
